/**
 * Language selection dropdown for the Templates accordion.
 * Visual style mirrors DropDownLogicalUnits so it blends with other
 * react-select based dropdowns used across the app.
 */
import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import { Label, Stack } from "@fluentui/react";
import { useCommonControlsStyle } from "../../../../theme/styles/CommonControlsStyle";
import { useTheme } from "../../../../theme/OAS365Theme";
const MENU_PORTAL_Z = 13000;

export const TEMPLATE_LANGUAGE_ALL_OPTION = { value: "ALL", label: "All" };

/** Default Templates panel language (English). */
export const TEMPLATE_DEFAULT_LANGUAGE_OPTION = {
  value: "en",
  label: "en - English",
};

const LanguageSelectionDropdown = ({
  languageCodes = [],
  value = null,
  onChange,
  menuPortal = true,
  placeholder = "Select language...",
  label = "Language Selection",
  noOptionsMessage = "No languages available",
}) => {
  const theme = useTheme();
  const styleTheme = useCommonControlsStyle();

  // The IFS FileTemplateSet projection exposes two related properties:
  //   - `Language`     -> formatted display string (e.g. "en - English"),
  //                       only usable for $apply/groupby aggregations.
  //   - `LanguageCode` -> the plain ISO code (e.g. "en"), which is the
  //                       filterable property used by the templates endpoint.
  // So the option label keeps the friendly "en - English" text, but the
  // value is just the code prefix that we send to the backend.
  const options = useMemo(() => {
    const languageOptions = !Array.isArray(languageCodes)
      ? []
      : languageCodes
          .map((item) => {
            const raw = item?.language ?? item?.Language ?? "";
            if (!raw) return null;
            const trimmed = String(raw).trim();
            const [codePart] = trimmed.split(" - ");
            const code = (codePart || trimmed).trim();
            return {
              value: code,
              label: trimmed,
            };
          })
          .filter(Boolean);

    return [TEMPLATE_LANGUAGE_ALL_OPTION, ...languageOptions];
  }, [languageCodes]);

  const isDarkMode =
    String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const menuBg = theme.colors.backgroundColor ?? "#fff";
  const borderGrey = theme.colors.Addovation20Grey ?? "#d8d8d8";
  const focusGreen = theme.colors.addovation70Green ?? "#4c8d6a";
  const optionHoverBg =
    theme.colors.msTeamsFilesHover ?? (isDarkMode ? "#343434" : "#EBEBEB");
  const optionSelectedBg = isDarkMode
    ? (theme.colors.dropDownSelectColor ?? "#3A3A3A")
    : (theme.colors.addovation10Grey ??
      theme.colors.Addovation20Grey ??
      "#EEEEEE");
  const scrollbarTrack =
    theme.colors.scrollbarBackground ?? (isDarkMode ? "#525252" : "#fff");
  const scrollbarThumb =
    theme.colors.scrollbarThumb ?? (isDarkMode ? "#848484" : "#ccc");
  const scrollbarSize = 10;
  const fontFamily = theme.fonts?.regular ?? "Poppins, sans-serif";
  const fontSize = `${theme.fontSizes?.normal ?? 12}px`;

  // Menu is portaled to document.body — inject scrollbar CSS so webkit rules apply reliably.
  useEffect(() => {
    const styleId = "language-select-menu-scrollbar";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      .language-select__menu-list {
        scrollbar-width: thin;
        scrollbar-color: ${scrollbarThumb} ${scrollbarTrack};
      }
      .language-select__menu-list::-webkit-scrollbar {
        width: ${scrollbarSize}px;
      }
      .language-select__menu-list::-webkit-scrollbar-track {
        background: ${scrollbarTrack};
        border-radius: ${scrollbarSize}px;
      }
      .language-select__menu-list::-webkit-scrollbar-thumb {
        background: ${scrollbarThumb};
        border-radius: ${scrollbarSize}px;
        border: 2px solid ${scrollbarTrack};
        min-height: 40px;
      }
      .language-select__menu-list::-webkit-scrollbar-thumb:hover {
        background: ${isDarkMode ? "#9a9a9a" : "#b3b3b3"};
      }
      .language-select__menu-list::-webkit-scrollbar-button {
        display: none;
        height: 0;
        width: 0;
      }
    `;
  }, [scrollbarTrack, scrollbarThumb, scrollbarSize, isDarkMode]);

  const customStyles = {
    control: (base, state) => ({
      ...base,
      fontFamily,
      fontSize,
      minHeight: 38,
      borderRadius: 8,
      backgroundColor: menuBg,
      borderColor: state.isFocused ? focusGreen : borderGrey,
      borderWidth: 1,
      boxShadow: "none",
      "&:hover": {
        borderColor: focusGreen,
      },
    }),
    placeholder: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      color: theme.colors.deactiveColor ?? "#9e9e9e",
    }),
    singleValue: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      color: theme.colors.fontColor,
    }),
    input: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      color: theme.colors.fontColor,
    }),
    valueContainer: (base) => ({
      ...base,
      paddingLeft: 10,
      paddingRight: 8,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: 6,
    }),
    menu: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      backgroundColor: menuBg,
      borderRadius: 8,
      marginTop: 6,
      marginBottom: 4,
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
      border: `1px solid ${borderGrey}`,
      overflow: "hidden",
      zIndex: MENU_PORTAL_Z,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: MENU_PORTAL_Z,
    }),
    menuList: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      paddingTop: 4,
      paddingBottom: 4,
      maxHeight: 260,
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily,
      fontSize,
      padding: "8px 12px",
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? optionSelectedBg
        : state.isFocused
          ? optionHoverBg
          : menuBg,
      color: theme.colors.fontColor,
      "&:active": {
        backgroundColor: optionHoverBg,
      },
    }),
    noOptionsMessage: (base) => ({
      ...base,
      fontFamily,
      fontSize,
      color: theme.colors.deactiveColor ?? "#9e9e9e",
    }),
  };

  return (
    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { width: "100%" } }}>
      <Stack.Item>
        <Label
          style={{
            ...styleTheme.LabelStyle,
            fontWeight: theme.fontWeight?.bold ?? 600,
          }}
        >
          {label}
        </Label>
      </Stack.Item>
      <Stack.Item>
        <Select
          className="basic-single"
          classNamePrefix="language-select"
          value={value}
          isClearable={false}
          name="languageCode"
          options={options}
          placeholder={placeholder}
          styles={customStyles}
          onChange={onChange}
          noOptionsMessage={() => noOptionsMessage}
          maxMenuHeight={260}
          menuPortalTarget={
            menuPortal && typeof document !== "undefined"
              ? document.body
              : null
          }
          menuPosition={menuPortal ? "fixed" : "absolute"}
          menuPlacement="auto"
          menuShouldBlockScroll={false}
          theme={(selectTheme) => ({
            ...selectTheme,
            borderRadius: 8,
            colors: {
              ...selectTheme.colors,
              primary: focusGreen,
              primary25: optionHoverBg,
              neutral0: menuBg,
              neutral80: theme.colors.fontColor,
            },
          })}
        />
      </Stack.Item>
    </Stack>
  );
};

export default LanguageSelectionDropdown;
